import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, api } from '../context/AppContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

const scoreProfile = form => {
  const checks = [
    !!form.name?.trim(),
    !!form.email?.trim() && isValidEmail(form.email),
    !!form.street?.trim(),
    !!form.city?.trim(),
    !!form.pincode?.trim(),
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
};

const inp = (err) => ({
  width: '100%', boxSizing: 'border-box', padding: '10px 14px',
  borderRadius: 12, fontSize: 13.5, color: '#111827', fontFamily: 'inherit',
  outline: 'none', border: `1.5px solid ${err ? '#fca5a5' : 'rgba(45,139,78,0.2)'}`,
  background: err ? '#fff5f5' : '#fff', transition: 'border-color 0.15s, box-shadow 0.15s',
});
const inpDisabled = {
  width: '100%', boxSizing: 'border-box', padding: '10px 14px',
  borderRadius: 12, fontSize: 13.5, color: '#374151', fontFamily: 'inherit',
  outline: 'none', border: '1.5px solid #f0f0f0',
  background: '#f9fafb', cursor: 'default',
};
const focus = e => { e.target.style.borderColor = '#2D8B4E'; e.target.style.boxShadow = '0 0 0 3px rgba(45,139,78,0.1)'; };
const blur  = (err, e) => { e.target.style.borderColor = err ? '#fca5a5' : 'rgba(45,139,78,0.2)'; e.target.style.boxShadow = 'none'; };

const Label = ({ children }) => (
  <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>
    {children}
  </label>
);

export default function ProfilePage() {
  const { user, updateUser } = useApp();
  const navigate = useNavigate();

  const [editing,    setEditing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [emailErr,   setEmailErr]   = useState('');
  const [locLoading, setLocLoading] = useState(false);
  const [locError,   setLocError]   = useState('');

  const [form, setForm] = useState({
    name:     user?.name                  || '',
    email:    user?.email                 || '',
    street:   user?.address?.street       || '',
    city:     user?.address?.city         || '',
    state:    user?.address?.state        || '',
    pincode:  user?.address?.pincode      || '',
    landmark: user?.address?.landmark     || '',
  });

  // Sync form when user changes (e.g. after save)
  // Sync form on initial mount once user loads from context
  useEffect(() => {
    if (user) {
      setForm({
        name:     user.name                  || '',
        email:    user.email                 || '',
        street:   user.address?.street       || '',
        city:     user.address?.city         || '',
        state:    user.address?.state        || '',
        pincode:  user.address?.pincode      || '',
        landmark: user.address?.landmark     || '',
      });
    }
  }, []); // eslint-disable-line

  const score = scoreProfile(form);
  const scoreColor = score < 40 ? '#ef4444' : score < 80 ? '#f59e0b' : '#22c55e';
  const scoreLabel = score < 40 ? 'Incomplete' : score < 80 ? 'Almost there' : score === 100 ? 'Complete ✓' : 'Good';

  const set = f => e => {
    setForm(p => ({ ...p, [f]: e.target.value }));
    if (f === 'email') {
      setEmailErr(e.target.value && !isValidEmail(e.target.value) ? 'Please enter a valid email' : '');
    }
  };

  const cancel = () => {
    setEditing(false);
    setEmailErr('');
    setLocError('');
    // reset form to current user
    if (user) {
      setForm({
        name:     user.name                  || '',
        email:    user.email                 || '',
        street:   user.address?.street       || '',
        city:     user.address?.city         || '',
        state:    user.address?.state        || '',
        pincode:  user.address?.pincode      || '',
        landmark: user.address?.landmark     || '',
      });
    }
  };

  const handleSave = async () => {
    if (form.email && !isValidEmail(form.email)) {
      setEmailErr('Please enter a valid email before saving.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/users/profile', {
        name:  form.name.trim(),
        email: form.email.trim(),
        address: {
          street:   form.street.trim(),
          city:     form.city.trim(),
          state:    form.state.trim(),
          pincode:  form.pincode.trim(),
          landmark: form.landmark.trim(),
        },
      });

      // ── Extract updated user from either { user:{} } or flat response ──
      const updated = res.data?.user ?? res.data;

      // 1. Update global context so Navbar banner disappears immediately
      updateUser(updated);

      // 2. Sync form directly from response — don't wait for useEffect chain
      setForm({
        name:     updated.name                || '',
        email:    updated.email               || '',
        street:   updated.address?.street     || '',
        city:     updated.address?.city       || '',
        state:    updated.address?.state      || '',
        pincode:  updated.address?.pincode    || '',
        landmark: updated.address?.landmark   || '',
      });

      setEditing(false);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const fetchLocation = () => {
    if (!navigator.geolocation) { setLocError('Location not supported by your browser.'); return; }
    setLocLoading(true); setLocError('');
    navigator.geolocation.getCurrentPosition(
      async pos => {
        try {
          const res  = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&addressdetails=1`, { headers: { 'Accept-Language': 'en' } });
          const data = await res.json();
          const a    = data.address || {};
          setForm(p => ({
            ...p,
            street:  [a.road, a.neighbourhood, a.suburb].filter(Boolean).join(', ') || p.street,
            city:    a.city || a.town || a.village || a.county || p.city,
            state:   a.state   || p.state,
            pincode: a.postcode || p.pincode,
          }));
          toast.success('Location detected! Please verify and save.');
        } catch { setLocError('Could not fetch address. Please enter manually.'); }
        finally { setLocLoading(false); }
      },
      err => {
        setLocLoading(false);
        setLocError(err.code === 1 ? 'Location permission denied.' : 'Could not get location. Try again.');
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()
    : user?.phone?.slice(-2) || '?';

  const CHECKS = [
    { label: 'Full name',   done: !!form.name?.trim() },
    { label: 'Email',       done: !!form.email?.trim() && isValidEmail(form.email) },
    { label: 'Street',      done: !!form.street?.trim() },
    { label: 'City',        done: !!form.city?.trim() },
    { label: 'Pincode',     done: !!form.pincode?.trim() },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafb', fontFamily: "'Roboto',sans-serif" }}>
      <Navbar />

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(135deg,#062c1b 0%,#0a3d1f 50%,#1a7040 100%)', padding: '28px 20px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(76,175,114,0.07)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, paddingBottom: 28 }}>
            {/* avatar */}
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#d97706,#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', flexShrink: 0, border: '3px solid rgba(255,255,255,0.2)' }}>
              {initials}
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ color: '#fff', fontWeight: 900, fontSize: 'clamp(18px,3vw,24px)', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
                {user?.name || <span style={{ opacity: 0.6, fontStyle: 'italic', fontWeight: 500 }}>Add your name</span>}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>📞 {user?.phone}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: `${scoreColor}25`, color: scoreColor, border: `1px solid ${scoreColor}40` }}>
                  {scoreLabel} · {score}%
                </span>
                {user?.role === 'admin' && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'rgba(251,191,36,0.2)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.35)' }}>Admin</span>
                )}
              </div>
            </div>
            {!editing && (
              <button onClick={() => setEditing(true)}
                style={{ padding: '8px 20px', borderRadius: 100, border: '1.5px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                ✏️ Edit Profile
              </button>
            )}
          </div>
          {/* progress bar tab */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.06)', borderRadius: '12px 12px 0 0', padding: '10px 16px', marginTop: -2 }}>
            <div style={{ flex: 1, height: 5, borderRadius: 100, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 100, background: scoreColor, width: `${score}%`, transition: 'width 0.6s ease' }} />
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 600, flexShrink: 0 }}>Profile {score}% complete</span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px 60px', boxSizing: 'border-box' }}>
        <div className="pf-grid">

          {/* ── Left: actions + checklist ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* quick actions */}
            <div style={{ background: '#fff', borderRadius: 18, padding: '18px 16px', border: '1px solid #f0f0f0', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.6px', margin: '0 0 12px' }}>Quick Actions</p>
              {[
                { label: 'My Orders',   icon: '📦', path: '/orders'   },
                { label: 'Browse Menu', icon: '🥤', path: '/products' },
                ...(user?.role === 'admin' ? [{ label: 'Admin Panel', icon: '⚙️', path: '/admin' }] : []),
              ].map(a => (
                <button key={a.path} onClick={() => navigate(a.path)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, border: '1px solid #f0f0f0', background: '#fafafa', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', marginBottom: 8, textAlign: 'left', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#2D8B4E'; e.currentTarget.style.color = '#2D8B4E'; e.currentTarget.style.background = 'rgba(45,139,78,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#f0f0f0'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#fafafa'; }}>
                  <span style={{ fontSize: 17 }}>{a.icon}</span> {a.label}
                </button>
              ))}
            </div>

            {/* checklist */}
            <div style={{ background: '#fff', borderRadius: 18, padding: '18px 16px', border: '1px solid #f0f0f0', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.6px', margin: '0 0 14px' }}>Profile Checklist</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CHECKS.map(({ label, done }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: done ? 'rgba(34,197,94,0.12)' : '#f3f4f6', border: `1.5px solid ${done ? '#22c55e' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: done ? '#22c55e' : '#d1d5db', flexShrink: 0, fontWeight: 900 }}>
                      {done ? '✓' : '○'}
                    </div>
                    <span style={{ fontSize: 13, color: done ? '#374151' : '#9ca3af', fontWeight: done ? 600 : 400, textDecoration: done ? 'line-through' : 'none' }}>{label}</span>
                  </div>
                ))}
              </div>
              {score < 100 && (
                <button onClick={() => setEditing(true)}
                  style={{ width: '100%', marginTop: 14, padding: '9px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#2D8B4E,#4CAF72)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 12px rgba(45,139,78,0.25)' }}>
                  Complete Profile →
                </button>
              )}
            </div>
          </div>

          {/* ── Right: form ── */}
          <div>
            <div style={{ background: '#fff', borderRadius: 18, padding: '24px', border: '1px solid #f0f0f0', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

              {/* form header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 900, color: '#111827', margin: '0 0 2px' }}>Personal Information</h2>
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                    {editing ? 'Make your changes and hit Save' : 'Your saved delivery details'}
                  </p>
                </div>
                {editing ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={cancel}
                      style={{ padding: '8px 18px', borderRadius: 100, border: '1.5px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={handleSave} disabled={saving || !!emailErr}
                      style={{ padding: '8px 20px', borderRadius: 100, border: 'none', background: saving ? '#d1d5db' : 'linear-gradient(135deg,#15803d,#22c55e)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: saving ? 'none' : '0 3px 12px rgba(21,128,61,0.3)', display: 'flex', alignItems: 'center', gap: 7, opacity: !!emailErr ? 0.5 : 1 }}>
                      {saving && <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.65s linear infinite', display: 'inline-block' }} />}
                      {saving ? 'Saving…' : '💾 Save Changes'}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditing(true)}
                    style={{ padding: '8px 18px', borderRadius: 100, border: '1.5px solid rgba(45,139,78,0.3)', background: 'rgba(45,139,78,0.05)', color: '#2D8B4E', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    ✏️ Edit
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

                {/* Name */}
                <div>
                  <Label>Full Name</Label>
                  {editing
                    ? <input value={form.name} onChange={set('name')} placeholder="Your full name" style={inp(false)} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={inpDisabled}>{form.name || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>

                {/* Email */}
                <div>
                  <Label>Email Address</Label>
                  {editing ? (
                    <>
                      <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com"
                        style={inp(emailErr)} onFocus={focus} onBlur={e => blur(emailErr, e)} />
                      {emailErr && <p style={{ fontSize: 11, color: '#ef4444', margin: '3px 0 0' }}>{emailErr}</p>}
                      {form.email && !emailErr && isValidEmail(form.email) && <p style={{ fontSize: 11, color: '#22c55e', margin: '3px 0 0' }}>✓ Valid</p>}
                    </>
                  ) : (
                    <div style={inpDisabled}>{form.email || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>
                  )}
                </div>

                {/* Address divider */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}>
                  <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>📍 Delivery Address</span>
                    {editing && (
                      <button onClick={fetchLocation} disabled={locLoading}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 100, border: 'none', background: 'linear-gradient(135deg,#2D8B4E,#4CAF72)', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: locLoading ? 0.6 : 1 }}>
                        {locLoading
                          ? <><span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.65s linear infinite', display: 'inline-block' }} /> Detecting…</>
                          : '📍 Use My Location'}
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
                </div>
                {locError && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 12px' }}>
                    ⚠️ {locError}
                  </div>
                )}

                {/* Street */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <Label>Street Address</Label>
                  {editing
                    ? <textarea value={form.street} onChange={set('street')} placeholder="Flat no, Building, Street…" rows={2}
                        style={{ ...inp(false), resize: 'none' }} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={{ ...inpDisabled, minHeight: 44 }}>{form.street || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>

                {/* City */}
                <div>
                  <Label>City</Label>
                  {editing
                    ? <input value={form.city} onChange={set('city')} placeholder="Bengaluru" style={inp(false)} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={inpDisabled}>{form.city || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>

                {/* State */}
                <div>
                  <Label>State</Label>
                  {editing
                    ? <input value={form.state} onChange={set('state')} placeholder="Karnataka" style={inp(false)} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={inpDisabled}>{form.state || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>

                {/* Pincode */}
                <div>
                  <Label>Pincode</Label>
                  {editing
                    ? <input value={form.pincode} onChange={e => set('pincode')({ target: { value: e.target.value.replace(/\D/g,'').slice(0,6) } })} placeholder="560038" style={inp(false)} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={inpDisabled}>{form.pincode || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>

                {/* Landmark */}
                <div>
                  <Label>Landmark (Optional)</Label>
                  {editing
                    ? <input value={form.landmark} onChange={set('landmark')} placeholder="Near Metro Station" style={inp(false)} onFocus={focus} onBlur={e => blur(false, e)} />
                    : <div style={inpDisabled}>{form.landmark || <span style={{ color: '#d1d5db' }}>Not set</span>}</div>}
                </div>
              </div>

              {/* bottom save bar */}
              {editing && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <p style={{ fontSize: 12, color: CHECKS.every(c => c.done) ? '#22c55e' : '#9ca3af', margin: 0 }}>
                    {CHECKS.every(c => c.done) ? '✅ All fields complete!' : `${CHECKS.filter(c => !c.done).length} field(s) still missing`}
                  </p>
                  <button onClick={handleSave} disabled={saving || !!emailErr}
                    style={{ padding: '10px 28px', borderRadius: 100, border: 'none', background: 'linear-gradient(135deg,#15803d,#22c55e)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 3px 12px rgba(21,128,61,0.28)', opacity: !!emailErr ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 7 }}>
                    {saving && <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.65s linear infinite', display: 'inline-block' }} />}
                    {saving ? 'Saving…' : '💾 Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .pf-grid {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 18px;
          align-items: start;
        }
        @media(max-width:768px) {
          .pf-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}